// src/pages/Dashboard/index.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    CardText,
    Badge,
    Spinner,
    Alert,
    Table,
    Button,
} from 'reactstrap';
import {
    Users,
    UserCheck,
    UserX,
    Calendar,
    Clock,
    TrendingUp,
    TrendingDown,
    PieChart,
    Activity,
    RefreshCw,
    CheckCircle,
    XCircle,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { dashboardApi, DashboardStats } from '../../api/dashboard';
import { format, subDays } from 'date-fns';

// Import charts
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

// Stat Card Component
interface StatCardProps {
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
    trend?: number;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle, trend }) => {
    return (
        <Card className="h-100">
            <CardBody>
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <h6 className="text-muted text-uppercase small mb-2">{title}</h6>
                        <h3 className="mb-0 fw-bold">{value}</h3>
                        {subtitle && <small className="text-muted">{subtitle}</small>}
                        {trend !== undefined && (
                            <div className="mt-1">
                                {trend > 0 ? (
                                    <Badge color="success" className="d-inline-flex align-items-center gap-1">
                                        <TrendingUp size={12} /> +{trend}%
                                    </Badge>
                                ) : trend < 0 ? (
                                    <Badge color="danger" className="d-inline-flex align-items-center gap-1">
                                        <TrendingDown size={12} /> {trend}%
                                    </Badge>
                                ) : null}
                            </div>
                        )}
                    </div>
                    <div className={`p-3 rounded-circle bg-${color} bg-opacity-10`}>
                        <span className={`text-${color}`}>{icon}</span>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

// Today Attendance Component
interface TodayAttendanceProps {
    presentEmployees: Array<{
        id: number;
        name: string;
        employee_code: string;
        designation: string | null;
        check_in_time: string;
        check_out_time: string | null;
        status: string;
    }>;
    absentEmployees: Array<{
        id: number;
        name: string;
        employee_code: string;
        designation: string | null;
    }>;
}

const TodayAttendanceList: React.FC<TodayAttendanceProps> = ({ presentEmployees, absentEmployees }) => {
    return (
        <Row className="g-3">
            {/* Present Employees - Left Side */}
            <Col lg={6}>
                <Card className="h-100">
                    <CardHeader className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 d-flex align-items-center gap-2">
                            <CheckCircle size={18} className="text-success" />
                            Present Employees
                            <Badge color="success" pill className="ms-2">
                                {presentEmployees.length}
                            </Badge>
                        </h6>
                    </CardHeader>
                    <CardBody style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {presentEmployees.length > 0 ? (
                            <div className="table-responsive">
                                <Table hover size="sm" className="mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Code</th>
                                            <th>Check In</th>
                                            {/* <th>Check Out</th>
                                            <th>Status</th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {presentEmployees.map((emp, index) => (
                                            <tr key={emp.id}>
                                                <td>{index + 1}</td>
                                                <td className="fw-semibold">{emp.name}</td>
                                                <td><code>{emp.employee_code}</code></td>
                                                <td>
                                                    <Badge color="success" pill>
                                                        {emp.check_in_time}
                                                    </Badge>
                                                </td>
                                                {/* <td>
                                                    {emp.check_out_time ? (
                                                        <Badge color="info" pill>
                                                            {emp.check_out_time}
                                                        </Badge>
                                                    ) : (
                                                        <Badge color="warning" pill>
                                                            Not Checked Out
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>
                                                    {emp.status === 'checked_out' ? (
                                                        <Badge color="success">Checked Out</Badge>
                                                    ) : (
                                                        <Badge color="warning">Checked In</Badge>
                                                    )}
                                                </td> */}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <UserCheck size={32} className="text-muted mb-2" />
                                <p className="text-muted mb-0">No employees present today</p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Col>

            {/* Absent Employees - Right Side */}
            <Col lg={6}>
                <Card className="h-100">
                    <CardHeader className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 d-flex align-items-center gap-2">
                            <XCircle size={18} className="text-danger" />
                            Absent Employees
                            <Badge color="danger" pill className="ms-2">
                                {absentEmployees.length}
                            </Badge>
                        </h6>
                    </CardHeader>
                    <CardBody style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {absentEmployees.length > 0 ? (
                            <div className="table-responsive">
                                <Table hover size="sm" className="mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Code</th>
                                            <th>Designation</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {absentEmployees.map((emp, index) => (
                                            <tr key={emp.id}>
                                                <td>{index + 1}</td>
                                                <td className="fw-semibold">{emp.name}</td>
                                                <td><code>{emp.employee_code}</code></td>
                                                <td>{emp.designation || 'N/A'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <UserX size={32} className="text-muted mb-2" />
                                <p className="text-muted mb-0">No employees absent today 🎉</p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

// Main Dashboard Component
const Dashboard: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [error, setError] = useState('');
    const [attendanceData, setAttendanceData] = useState<any[]>([]);

    // Fetch dashboard data
    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await dashboardApi.getStats();
            if (response.success) {
                console.log('Dashboard stats fetched successfully:', response.data);
                setStats(response.data);
                
                // Also fetch attendance data for the last 7 days
                await fetchLast7DaysAttendance();
            } else {
                setError(response.message || 'Failed to fetch dashboard data');
                toast.error('Failed to fetch dashboard data');
            }
        } catch (error: any) {
            console.error('Error fetching dashboard data:', error);
            setError(error.message || 'Failed to fetch dashboard data');
            toast.error('Failed to fetch dashboard data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    // Fetch attendance for the last 7 days
    const fetchLast7DaysAttendance = useCallback(async () => {
        try {
            const today = new Date();
            const promises = [];
            
            // Get attendance for the last 7 days
            for (let i = 6; i >= 0; i--) {
                const date = subDays(today, i);
                const dateStr = format(date, 'yyyy-MM-dd');
                promises.push(
                    dashboardApi.getAttendanceByDate(dateStr)
                        .then(response => ({
                            date: dateStr,
                            present: response.data?.employees?.filter((e: any) => e.status === 'present').length || 0,
                            total: response.data?.employees?.length || 0,
                        }))
                        .catch(() => ({
                            date: dateStr,
                            present: 0,
                            total: 0,
                        }))
                );
            }
            
            const results = await Promise.all(promises);
            setAttendanceData(results);
        } catch (error) {
            console.error('Error fetching attendance data:', error);
        }
    }, []);

    // Refresh data
    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchDashboardData();
    };

    // Initial fetch
    useEffect(() => {
        fetchDashboardData();
    }, []);

    // Prepare monthly trend chart - NOW USING REAL DATA
    const getMonthlyChartData = () => {
        if (attendanceData.length === 0) {
            return {
                labels: ['No Data'],
                datasets: [
                    {
                        label: 'Present',
                        data: [0],
                        borderColor: '#28A745',
                        backgroundColor: 'rgba(40, 167, 69, 0.1)',
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#28A745',
                    },
                ],
            };
        }

        return {
            labels: attendanceData.map(d => format(new Date(d.date), 'dd MMM')),
            datasets: [
                {
                    label: 'Present',
                    data: attendanceData.map(d => d.present),
                    borderColor: '#28A745',
                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#28A745',
                },
                {
                    label: 'Absent',
                    data: attendanceData.map(d => d.total - d.present),
                    borderColor: '#DC3545',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#DC3545',
                },
            ],
        };
    };

    // Get department chart data
    const getDepartmentChartData = () => {
        if (!stats || !stats.department_stats || stats.department_stats.length === 0) {
            return {
                labels: ['No Data'],
                datasets: [
                    {
                        data: [1],
                        backgroundColor: ['#e9ecef'],
                        borderWidth: 0,
                    },
                ],
            };
        }

        const colors = [
            '#4F81BD', '#28A745', '#FFC107', '#DC3545', '#17A2B8',
            '#6F42C1', '#FD7E14', '#20C997', '#E83E8C', '#6C757D',
        ];

        return {
            labels: stats.department_stats.map((d) => d.designation),
            datasets: [
                {
                    data: stats.department_stats.map((d) => d.count),
                    backgroundColor: colors.slice(0, stats.department_stats.length),
                    borderWidth: 2,
                    borderColor: '#fff',
                },
            ],
        };
    };

    // Chart options
    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'circle',
                },
            },
        },
        cutout: '70%',
    };

    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 20,
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 10,
                },
            },
        },
    };

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="text-center py-5">
                        <Spinner color="primary" className="me-2" size="lg" />
                        <span className="ms-2">Loading dashboard...</span>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <BreadCrumb
                        title="Dashboard"
                        pageTitle="Home"
                    />
                    <Button
                        color="secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={`me-1 ${refreshing ? 'spin' : ''}`} />
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </Button>
                </div>

                {error && (
                    <Alert color="danger" className="mb-3">
                        <span className="me-2">⚠️</span>
                        {error}
                    </Alert>
                )}

                {stats && (
                    <>
                        {/* Stats Row */}
                        <Row className="g-3 mb-3">
                            <Col xl={4} md={6}>
                                <StatCard
                                    title="Total Employees"
                                    value={stats.employee_stats.total}
                                    icon={<Users size={24} />}
                                    color="primary"
                                    subtitle={`${stats.employee_stats.active} active`}
                                />
                            </Col>
                            <Col xl={4} md={6}>
                                <StatCard
                                    title="Present Today"
                                    value={stats.today_attendance.present}
                                    icon={<UserCheck size={24} />}
                                    color="success"
                                    subtitle={`of ${stats.today_attendance.total_employees} employees`}
                                />
                            </Col>
                            <Col xl={4} md={6}>
                                <StatCard
                                    title="Absent Today"
                                    value={stats.today_attendance.absent}
                                    icon={<UserX size={24} />}
                                    color="danger"
                                    subtitle={`of ${stats.today_attendance.total_employees} employees`}
                                />
                            </Col>
                        </Row>

                        {/* Today Attendance List - NEW SECTION */}
                        <Row className="mb-3">
                            <Col>
                                <Card>
                                    <CardHeader>
                                        <h6 className="mb-0">Today's Attendance Details</h6>
                                    </CardHeader>
                                    <CardBody>
                                        <TodayAttendanceList
                                            presentEmployees={stats.today_attendance.present_employees || []}
                                            absentEmployees={stats.today_attendance.absent_employees || []}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        {/* Charts Row - Attendance Trend + Department Distribution */}
                        <Row className="g-3 mb-3">
                            <Col lg={8}>
                                <Card>
                                    <CardHeader>
                                        <h6 className="mb-0">Attendance Trend (Last 7 Days)</h6>
                                    </CardHeader>
                                    <CardBody>
                                        <div style={{ height: '280px' }}>
                                            {getMonthlyChartData() && (
                                                <Line
                                                    data={getMonthlyChartData()!}
                                                    options={lineOptions}
                                                />
                                            )}
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col lg={4}>
                                <Card className="h-100">
                                    <CardHeader>
                                        <h6 className="mb-0">Department Distribution</h6>
                                    </CardHeader>
                                    <CardBody>
                                        <div style={{ height: '240px' }}>
                                            <Doughnut
                                                data={getDepartmentChartData()}
                                                options={doughnutOptions}
                                            />
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        {/* Quick Stats Footer */}
                        <Row>
                            <Col>
                                <Card className="bg-light border-0">
                                    <CardBody className="py-2">
                                        <div className="d-flex justify-content-around text-center flex-wrap gap-2">
                                            <div>
                                                <small className="text-muted d-block">Total Employees</small>
                                                <strong>{stats.employee_stats.total}</strong>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Active</small>
                                                <strong className="text-success">{stats.employee_stats.active}</strong>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Inactive</small>
                                                <strong className="text-danger">{stats.employee_stats.inactive}</strong>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Resigned</small>
                                                <strong className="text-warning">{stats.employee_stats.resigned}</strong>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Terminated</small>
                                                <strong className="text-secondary">{stats.employee_stats.terminated}</strong>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>

            {/* Add spinning animation */}
            <style>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .sticky-top {
                    position: sticky;
                    top: 0;
                    z-index: 1;
                }
            `}</style>
        </div>
    );
};

export default Dashboard;